import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Bed, Bath, Maximize, MapPin, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PropertyGallery } from "@/components/PropertyGallery";
import { PropertyContactForm } from "@/components/PropertyContactForm";
import { VirtualTour } from "@/components/VirtualTour";
import { FloorPlan } from "@/components/FloorPlan";
import { getPropertyById } from "@/data/properties";
import { Helmet } from "react-helmet-async";
import { PageTransition } from "@/components/PageTransition";
import { useRegion } from "@/contexts/RegionContext";

const PropertyDetail = () => {
  const { id } = useParams<{ id: string }>();
  const property = getPropertyById(id || "");
  const { formatArea, areaUnit, formatPrice } = useRegion();

  if (!property) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl text-foreground mb-4">Property Not Found</h1>
          <Link to="/properties">
            <Button variant="luxury">Return to Properties</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <PageTransition>
      <Helmet>
        <title>{property.title} | Agota Viršilaitė Luxury Real Estate</title>
        <meta
          name="description"
          content={`${property.title} in ${property.location}. ${property.beds} bedrooms, ${property.baths} bathrooms, ${formatArea(property.sqm)} ${areaUnit}. ${formatPrice(property.price)}`}
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navbar />

        {/* Hero Header */}
        <section className="pt-32 pb-12 bg-gradient-to-b from-secondary/50 to-background">
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Back Link */}
              <Link
                to="/properties"
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold transition-colors font-body mb-8"
              >
                <ArrowLeft size={18} />
                Back to Properties
              </Link>

              {/* Title & Location */}
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 text-gold mb-3">
                    <MapPin size={16} />
                    <span className="font-body text-sm uppercase tracking-[0.2em]">
                      {property.location}
                    </span>
                  </div>
                  <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground">
                    {property.title}
                  </h1>
                  {property.partnerReferral && (
                    <div className="mt-3">
                      <a
                        href={property.partnerReferral.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-muted/50 border border-border/50 px-4 py-1.5 rounded-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <span className="text-xs uppercase tracking-wider font-medium">Referral Partner:</span>
                        <span className="font-medium text-foreground">{property.partnerReferral.name}</span>
                      </a>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-body text-sm text-muted-foreground mb-1">Asking Price</p>
                  <p className="font-display text-3xl lg:text-4xl text-gold">{formatPrice(property.price)}</p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-8 mt-8 pt-8 border-t border-border/30">
                <div className="flex items-center gap-3">
                  <Bed className="text-gold" size={22} />
                  <span className="font-body text-foreground">{property.beds} Bedrooms</span>
                </div>
                <div className="flex items-center gap-3">
                  <Bath className="text-gold" size={22} />
                  <span className="font-body text-foreground">{property.baths} Bathrooms</span>
                </div>
                <div className="flex items-center gap-3">
                  <Maximize className="text-gold" size={22} />
                  <span className="font-body text-foreground">{formatArea(property.sqm)} {areaUnit}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="py-12">
          <div className="container mx-auto px-6 lg:px-12">
            <PropertyGallery images={property.gallery} title={property.title} />
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12 lg:py-20">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Left Column - Details */}
              <div className="lg:col-span-2 space-y-16">
                {/* Description */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="font-display text-3xl text-foreground mb-6">About This Property</h2>
                  <p className="font-body text-muted-foreground leading-relaxed text-lg">
                    {property.description}
                  </p>
                </motion.div>

                {/* Features */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="font-display text-3xl text-foreground mb-6">Key Features</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {property.features.map((feature) => (
                      <div
                        key={feature}
                        className="flex items-center gap-3 p-4 bg-secondary/30 border border-border/30"
                      >
                        <Check className="text-gold flex-shrink-0" size={18} />
                        <span className="font-body text-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Virtual Tour */}
                <div>
                  <h2 className="font-display text-3xl text-foreground mb-6">Virtual Tour</h2>
                  <VirtualTour
                    tourUrl={property.virtualTourUrl}
                    title={property.title}
                    previewImage={property.image}
                  />
                </div>

                {/* Floor Plan */}
                <FloorPlan floorPlan={property.floorPlan} />
              </div>

              {/* Right Column - Contact Form */}
              <div className="lg:col-span-1">
                <div className="sticky top-32">
                  <PropertyContactForm propertyTitle={property.title} />
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </PageTransition>
  );
};

export default PropertyDetail;
